export function useElementFullscreen(target: Ref<HTMLElement | null>) {
  const isFullscreen = ref(false);
  const isFullscreenSupported = ref(false);

  function updateState(): void {
    if (!import.meta.client) {
      return;
    }

    const element = target.value;
    isFullscreenSupported.value = Boolean(
      typeof element?.requestFullscreen === "function" &&
      typeof document.exitFullscreen === "function",
    );
    isFullscreen.value = Boolean(
      element && document.fullscreenElement === element,
    );
  }

  async function exitFullscreen(): Promise<void> {
    if (
      !import.meta.client ||
      !target.value ||
      document.fullscreenElement !== target.value ||
      typeof document.exitFullscreen !== "function"
    ) {
      return;
    }

    try {
      await document.exitFullscreen();
    } catch {
      // The browser can revoke fullscreen while the component is unmounting.
    } finally {
      updateState();
    }
  }

  async function toggleFullscreen(): Promise<void> {
    if (!import.meta.client || !target.value) {
      return;
    }

    if (document.fullscreenElement === target.value) {
      await exitFullscreen();
      return;
    }

    if (typeof target.value.requestFullscreen !== "function") {
      updateState();
      return;
    }

    try {
      await target.value.requestFullscreen();
    } catch {
      // Fullscreen can be denied by browser policy even after a user gesture.
    } finally {
      updateState();
    }
  }

  onMounted(() => {
    updateState();
    document.addEventListener("fullscreenchange", updateState);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("fullscreenchange", updateState);
    // Removing an active fullscreen element has inconsistent browser behavior;
    // leave explicitly so the surrounding page always recovers its layout.
    exitFullscreen().catch(() => undefined);
  });

  watch(target, () => {
    updateState();
  });

  return {
    isFullscreen,
    isFullscreenSupported,
    toggleFullscreen,
    exitFullscreen,
  };
}
